/**
 * Confirmation dialog for writing off / marking-as-billed-externally a
 * set of timesheets. Leaves an audit trail via the ``reason`` field.
 *
 * Built as a controlled dialog so the parent can pass in the set of
 * selected IDs and react to completion (refresh list, clear selection).
 */
import { useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/hooks/use-toast"
import { writeOffItems } from "@/services/invoicing"

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  timesheetIds: number[]
  costIds?: number[]
  onDone?: () => void
}

export function WriteOffDialog({
  open,
  onOpenChange,
  timesheetIds,
  costIds = [],
  onDone,
}: Props) {
  const { toast } = useToast()
  const [reason, setReason] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const count = timesheetIds.length + costIds.length

  async function submit() {
    if (count === 0) return
    setSubmitting(true)
    try {
      const result = await writeOffItems({
        timesheet_ids: timesheetIds,
        cost_ids: costIds,
        reason,
      })
      toast({
        title: "Written off",
        description: `${result.timesheets} timesheet(s) and ${result.costs} cost(s) flagged as billed externally.`,
      })
      setReason("")
      onOpenChange(false)
      onDone?.()
    } catch (e: any) {
      toast({
        title: "Write-off failed",
        description: e?.message ?? "See logs.",
        variant: "destructive",
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Write off {count} item{count === 1 ? "" : "s"}</DialogTitle>
          <DialogDescription>
            Marks the selected work as billed externally. These items drop
            out of WIP and are hidden from the drifting-timesheet anomaly
            detector. They are <strong>not</strong> linked to any Sentra
            invoice — use "Attach items" on an existing invoice if that's
            what you need instead.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="write-off-reason">Reason (audit trail)</Label>
          <Textarea
            id="write-off-reason"
            placeholder="e.g. Billed outside Sentra on paper invoice #A-0021"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
          />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting || count === 0}>
            {submitting ? "Writing off…" : "Confirm write-off"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
